import { roundCeil, roundFloor } from '../../helpers/roundToDecimal';
import { FinanceProperties, FinanceTermTypes, HitachiFinanceTerm } from '../../types';

export function mapByAddingMinAndMaxDepositAmount(terms: HitachiFinanceTerm[], price: number): HitachiFinanceTerm[] {
  return terms.map((term: HitachiFinanceTerm) => {
    const maxDepositAmount = (price * term.maxDeposit) / 100;
    return {
      ...term,
      maxDepositAmount: price - maxDepositAmount >= term.minLoanAmount ? maxDepositAmount : price - term.minLoanAmount,
      minDepositAmount: (price * term.minDeposit) / 100,
      defaultDepositAmount: (price * term.defaultDeposit) / 100
    };
  });
}

export function calcMaxDeposit(terms: HitachiFinanceTerm[]): number | null {
  if (terms.length === 0) {
    return null;
  }
  return Math.floor(
    terms.reduce((acc: HitachiFinanceTerm, curr: HitachiFinanceTerm) =>
      acc.maxDepositAmount >= curr.maxDepositAmount ? acc : curr
    ).maxDepositAmount
  );
}

export function filterByDepositValue(terms: HitachiFinanceTerm[], currentDeposit: number): HitachiFinanceTerm[] {
  return terms.filter(
    (term) => isTermInRange(term, currentDeposit) && term.type !== FinanceTermTypes.DeferredInterestFreeCredit
  );
}

export function findDeferredTerm(terms: HitachiFinanceTerm[], currentDeposit: number): HitachiFinanceTerm | null {
  return (
    terms.find(
      (term) => isTermInRange(term, currentDeposit) && term.type === FinanceTermTypes.DeferredInterestFreeCredit
    ) || null
  );
}

function isTermInRange(term: HitachiFinanceTerm, currentDeposit: number): boolean {
  return term.maxDepositAmount >= currentDeposit;
}

export function calcHitachiFinanceProperties(
  term: HitachiFinanceTerm,
  price: number,
  currentDeposit = 0
): FinanceProperties {
  const creditAmount = roundFloor(price - currentDeposit, -2);
  const isIBC = term.type === FinanceTermTypes.InterestBearingCredit && term.yield !== 0;
  const installmentMultiplier = isIBC ? term.instalmentMultiplier : 1 / term.term;
  const monthlyAmount = creditAmount * installmentMultiplier;
  const monthlyRepayment = isIBC ? roundCeil(monthlyAmount, -2) : roundFloor(monthlyAmount, -2);
  const interestPayable = term.apr / 100 === 0 ? 0 : monthlyRepayment * term.term - creditAmount;

  return {
    monthlyRepayment,
    creditAmount,
    interestPayable,
    totalPayable: price + interestPayable
  };
}
